import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api, errorMsg } from "@/lib/api";
import { useCotizacion } from "@/hooks/useCotizacion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TablaRepuestosCot } from "@/components/cotizacion/TablaRepuestosCot";
import { ManoObraSection } from "@/components/cotizacion/ManoObraSection";
import { SelectorPlantillaDialog } from "@/components/cotizacion/SelectorPlantillaDialog";
import { ManoObraDialog } from "@/components/cotizacion/ManoObraDialog";
import { EquivalentesDialog } from "@/components/cotizacion/EquivalentesDialog";
import { OpcionalesDialog } from "@/components/cotizacion/OpcionalesDialog";
import { ArrowLeft, FileDown, Copy as CopyIcon, Printer, Download, Search, ClipboardList } from "lucide-react";

export default function CotizacionDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { cot, cargar, agregarRepuesto, cambiarCantidad, eliminarItem, sustituirItem, cargarPlantilla,
    guardarManoObra, eliminarManoObra, exportarPdf, copiarLista } = useCotizacion(id);
  const [busqueda, setBusqueda] = useState("");
  const [gruposTarifa, setGruposTarifa] = useState([]);
  useEffect(() => { api.get("/grupos-tarifa").then((r) => setGruposTarifa(r.data)).catch(() => {}); }, []);
  const [resultados, setResultados] = useState([]);
  const [plantillasDialog, setPlantillasDialog] = useState(false);
  const [plantillas, setPlantillas] = useState([]);
  const [moDialog, setMoDialog] = useState(null);
  const [eqDialog, setEqDialog] = useState(null);
  const [opcionalesData, setOpcionalesData] = useState(null);
  const [catalogo, setCatalogo] = useState([]);
  const location = useLocation();

  useEffect(() => {
    if (location.state?.opcionales) {
      setOpcionalesData(location.state.opcionales);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  useEffect(() => {
    api.get("/repuestos").then((r) => setCatalogo(r.data)).catch(() => {});
  }, []);

  const equivalencia = useMemo(() => {
    const grupoDe = {};
    const conteo = {};
    for (const r of catalogo) {
      const gid = r.grupo_equivalencia_id;
      if (!gid) continue;
      grupoDe[r.id] = gid;
      conteo[gid] = (conteo[gid] || 0) + 1;
    }
    return { grupoDe, conteo };
  }, [catalogo]);

  const tieneEquivalentes = (it) => {
    const gid = equivalencia.grupoDe[it.repuesto_id];
    return !!gid && equivalencia.conteo[gid] > 1;
  };

  const sustituir = async (eq) => {
    if (await sustituirItem(eqDialog.id, eq.id)) {
      toast.success(`Sustituido por ${eq.codigo_parte}`);
      setEqDialog(null);
    }
  };

  useEffect(() => {
    if (!busqueda) { setResultados([]); return; }
    const t = setTimeout(async () => {
      const params = { q: busqueda };
      if (cot?.modelo_id) params.modelo_id = cot.modelo_id;
      const { data } = await api.get("/repuestos", { params });
      setResultados(data.slice(0, 8));
    }, 250);
    return () => clearTimeout(t);
  }, [busqueda, cot?.modelo_id]);

  const seleccionarRepuesto = async (rep) => {
    if (await agregarRepuesto(rep)) setBusqueda("");
  };

  const abrirPlantillas = async () => {
    const { data } = await api.get("/plantillas");
    setPlantillas(data); setPlantillasDialog(true);
  };

  const seleccionarPlantilla = async (pid) => {
    if (await cargarPlantilla(pid)) {
      setPlantillasDialog(false);
      try {
        // detalle bajo demanda: el listado ya no trae items_efectivos
        const { data: p } = await api.get(`/plantillas/${pid}`);
        const opcionales = (p.items_efectivos || p.items || []).filter((it) => it.opcional);
        if (opcionales.length) setOpcionalesData({ titulo: p.nombre, items: opcionales });
      } catch (e) { /* plantilla no accesible */ }
    }
  };

  const confirmarOpcionales = async (elegidos) => {
    for (const it of elegidos) {
      const { data } = await api.post(`/cotizaciones/${id}/items`, { repuesto_id: it.repuesto_id, cantidad: it.cantidad });
      if (data.sugerencia_operacion) {
        toast.info(`Sugerencia: este repuesto pertenece a la operación "${data.sugerencia_operacion.nombre}". Considere cargar la operación combinada para no duplicar el desarme.`, { duration: 8000 });
      }
    }
    await cargar();
    setOpcionalesData(null);
    toast.success(`${elegidos.length} repuesto(s) opcional(es) agregado(s)`);
  };

  const guardarMo = async () => {
    if (await guardarManoObra(moDialog)) setMoDialog(null);
  };

  const cargarTareas = async () => {
    try {
      const { data } = await api.post(`/cotizaciones/${id}/cargar-tareas-trabajo`);
      await cargar();
      const partes = [];
      if (data.cargadas.length) partes.push(`Cargadas: ${data.cargadas.join(", ")}`);
      if (data.ya_cargadas.length) partes.push(`Ya estaban: ${data.ya_cargadas.join(", ")}`);
      if (data.sin_plantilla.length) partes.push(`Sin plantilla: ${data.sin_plantilla.join(", ")}`);
      toast.success(partes.join(" · ") || "Sin tareas con plantilla para cargar");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al cargar tareas"); }
  };

  const aprobarItem = async (item, aprobado) => {
    try {
      await api.post(`/cotizaciones/${id}/items/${item.id}/aprobar`, { aprobado });
      await cargar();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  if (!cot) return <div className="text-sm text-muted-foreground">Cargando...</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 no-print">
        <div className="flex items-center gap-2 min-w-0">
          <Button variant="ghost" size="icon" data-testid="volver-btn" onClick={() => navigate("/cotizaciones")}><ArrowLeft size={17} /></Button>
          <div className="min-w-0">
            <h1 className="font-heading text-xl font-bold truncate" data-testid="cotizacion-titulo">{cot.nombre}</h1>
            <p className="text-xs text-muted-foreground">{cot.fecha_creacion ? cot.fecha_creacion.slice(0, 10).split("-").reverse().join("/") : ""}
              {cot.trabajo_id && (
                <button data-testid="cotizacion-trabajo-link" onClick={() => navigate(`/trabajos/${cot.trabajo_id}/finalizar`)}
                  className="ml-2 text-[10px] bg-indigo-600 text-white px-1.5 py-0.5 rounded uppercase font-bold hover:bg-indigo-700">
                  Ligada a trabajo
                </button>
              )}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="copiar-lista-btn" variant="outline" size="sm" onClick={copiarLista}><CopyIcon size={14} className="mr-1" /> Copiar</Button>
          <Button data-testid="imprimir-btn" variant="outline" size="sm" onClick={() => window.print()}><Printer size={14} className="mr-1" /> Imprimir</Button>
          <Button data-testid="exportar-pdf-btn" size="sm" onClick={exportarPdf}><FileDown size={14} className="mr-1" /> PDF</Button>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex flex-wrap gap-2 no-print">
          {!cot.modelo_id && (
            <select data-testid="cot-grupo-tarifa-select" value={cot.grupo_tarifa_id || ""}
              onChange={async (e) => {
                try {
                  await api.patch(`/cotizaciones/${id}`, { nombre: cot.nombre, notas: cot.notas || "", grupo_tarifa_id: e.target.value || null });
                  await cargar();
                  toast.success("Grupo de tarifa aplicado");
                } catch (err) { toast.error(errorMsg(err)); }
              }}
              className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
              <option value="">Tarifa M.O.: sin grupo</option>
              {gruposTarifa.map((g) => <option key={g.id} value={g.id}>{g.nombre} (₡{g.tarifa_hora}/h)</option>)}
            </select>
          )}
          <Button data-testid="cargar-plantilla-btn" variant="secondary" size="sm" onClick={abrirPlantillas}>
            <Download size={14} className="mr-1" /> Cargar plantilla
          </Button>
          {cot.trabajo_id && (
            <Button data-testid="cargar-tareas-trabajo-btn" variant="outline" size="sm"
              className="border-teal-500 text-teal-600 hover:bg-teal-50 hover:text-teal-700"
              onClick={cargarTareas}>
              <ClipboardList size={14} className="mr-1" /> Cargar tareas del trabajo
            </Button>
          )}
          <div className="relative flex-1 min-w-56">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="buscar-repuesto-cot-input" className="pl-8 h-9" placeholder="Buscar repuesto para agregar..."
              value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
            {resultados.length > 0 && (
              <div className="absolute z-50 top-full left-0 right-0 bg-white border border-border rounded-sm shadow-md max-h-56 overflow-y-auto">
                {resultados.map((r) => (
                  <button key={r.id} data-testid={`agregar-repuesto-${r.codigo_parte}`}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-secondary"
                    onClick={() => seleccionarRepuesto(r)}>
                    <span className="font-mono font-semibold text-xs">{r.codigo_parte}</span> — {r.nombre}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <TablaRepuestosCot items={cot.items} onCambiarCantidad={cambiarCantidad} onEliminar={eliminarItem}
          tieneEquivalentes={tieneEquivalentes} onVerEquivalentes={setEqDialog}
          aprobables={!!cot.trabajo_id} onAprobar={aprobarItem} />

        <ManoObraSection manoObra={cot.mano_obra} total={cot.total_mano_obra}
          onAgregar={() => setMoDialog({ descripcion: "", monto: "" })}
          onEditar={(m) => setMoDialog({ ...m })} onEliminar={eliminarManoObra} />
      </div>

      <SelectorPlantillaDialog open={plantillasDialog} onOpenChange={setPlantillasDialog}
        plantillas={plantillas} onSelect={seleccionarPlantilla} />

      <ManoObraDialog moDialog={moDialog} setMoDialog={setMoDialog} onGuardar={guardarMo} />

      <EquivalentesDialog item={eqDialog} onClose={() => setEqDialog(null)} onSustituir={sustituir} />

      <OpcionalesDialog data={opcionalesData} onClose={() => setOpcionalesData(null)} onConfirm={confirmarOpcionales} />
    </div>
  );
}
